/**
 * VA-27852 Phase 4 — production loader for the frontend Silero VAD runtime.
 *
 * WHAT THIS IS. The single place that knows onnxruntime-web exists. It loads the
 * self-hosted ORT runtime, configures it, creates one inference session for the
 * exact vendored model, and hands back a `SileroRuntime` — the three-method seam
 * `silero-vad-stream.ts` already consumes. The stream stays free of ORT, of the
 * DOM and of React; every browser and environment concern lives here.
 *
 * WHY IT LOADS ORT WITH A SCRIPT TAG RATHER THAN AN IMPORT. This is the decision
 * Phase 1 made deliberately and Phase 3 proved, and it is easy to "tidy" back
 * into a bug. ORT creates its proxy Worker as `new Worker(<url of the ORT
 * script>)`, deriving that URL from `document.currentScript.src`. Served from our
 * own origin at /ort/, that is a same-origin classic worker and it just works.
 * Bundled through webpack instead, the URL becomes our application chunk and the
 * bundler's worker detection does not match ORT's minified, aliased
 * `new URL(...)` construction — so the proxy silently fails to start and every
 * inference lands on the main thread. The feasibility spike measured that mode
 * starving the UI to ONE TIMER TICK IN FOUR SECONDS. Nothing throws. The
 * interview simply stops responding while the assistant speaks.
 *
 * So: no `import 'onnxruntime-web'` anywhere in src/. The dependency exists to
 * feed CopyWebpackPlugin, exactly as `monaco-editor` does, and this module is the
 * direct analogue of `prewarmMonaco()` in `coding-editor.tsx` — same self-hosting
 * rationale, same single-attempt-with-watchdog shape, same absence of a retry
 * loop.
 *
 * FAIL CLOSED. If anything here fails, the caller gets a rejected promise and no
 * runtime. There is no substitute VAD, no RMS fallback, and no fake runtime
 * standing in for a real one. The integration phase turns "no runtime" into
 * "barge-in unavailable", which is the behaviour this product had before the
 * feature existed.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** Self-hosted by CopyWebpackPlugin in webpack.{dev,prod}.ts. Never a CDN. */
export const ORT_SCRIPT_URL = '/ort/ort.wasm.min.js';
/**
 * Where ORT fetches `ort-wasm-simd-threaded.{mjs,wasm}`. Must be the same build
 * as the script above — a mismatched pair fails initialisation.
 */
export const ORT_WASM_PATHS = '/ort/';
/** The exact vendored model. Content-hashed name; see `silero-model-asset.test.ts`. */
export const SILERO_MODEL_URL = '/vad/silero_vad_16k.7ed98dd.onnx';
/** Documentation constant: sha256 of the model this loader expects to fetch. */
export const SILERO_MODEL_SHA256 = '7ed98ddbad84ccac4cd0aeb3099049280713df825c610a8ed34543318f1b2c49';
/** Watchdog for script load and session creation combined. */
export const SILERO_INIT_TIMEOUT_MS = 30000;
/**
 * Bounded, and bounded deliberately. A first failure may be a cold cache or a
 * transient fetch error, so one retry is worth having; beyond that the cause is
 * structural (blocked worker, missing asset, unsupported engine) and retrying
 * only burns battery and fills the console. There is no loop and no timer here —
 * a retry happens only if a caller asks again.
 */
export const SILERO_MAX_INIT_ATTEMPTS = 2;
const state = {
    status: 'idle',
    attempts: 0,
    refs: 0,
    error: null,
    pending: null,
    session: null,
    ort: null,
};
export function sileroLoaderSnapshot() {
    return {
        status: state.status,
        attempts: state.attempts,
        refs: state.refs,
        error: state.error,
    };
}
/** Test-only. Production never calls this; module state is per-page otherwise. */
export function __resetSileroLoaderForTests() {
    state.status = 'idle';
    state.attempts = 0;
    state.refs = 0;
    state.error = null;
    state.pending = null;
    state.session = null;
    state.ort = null;
}
function ortGlobal() {
    return globalThis.ort;
}
function withTimeout(work, ms, label) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`silero: ${label} timed out after ${ms}ms`)), ms);
        work.then((value) => {
            clearTimeout(timer);
            resolve(value);
        }, (error) => {
            clearTimeout(timer);
            reject(error);
        });
    });
}
/**
 * Inject the self-hosted ORT script and resolve once the global appears.
 *
 * If a global is already present the script is NOT injected again — the runtime
 * is a singleton per page, and a second copy would create a second proxy worker.
 */
function loadOrtGlobal() {
    const existing = ortGlobal();
    if (existing)
        return Promise.resolve(existing);
    if (typeof document === 'undefined') {
        return Promise.reject(new Error('silero: no document; ORT cannot be loaded here'));
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = ORT_SCRIPT_URL;
        script.async = true;
        // Same-origin by construction, which is what keeps ORT on the plain
        // `new Worker(url)` path instead of the cross-origin blob-worker fallback.
        script.crossOrigin = 'anonymous';
        script.onload = () => {
            const loaded = ortGlobal();
            if (loaded)
                resolve(loaded);
            else
                reject(new Error(`silero: ${ORT_SCRIPT_URL} loaded but exposed no ORT global`));
        };
        script.onerror = () => reject(new Error(`silero: failed to load ${ORT_SCRIPT_URL}`));
        document.head.appendChild(script);
    });
}
function createSession() {
    return __awaiter(this, void 0, void 0, function* () {
        const ort = yield loadOrtGlobal();
        state.ort = ort;
        // Configuration order matters: these must be set before the session is created.
        ort.env.wasm.wasmPaths = ORT_WASM_PATHS;
        // Single-threaded. ORT would choose 1 anyway without crossOriginIsolated, but
        // saying so explicitly means the behaviour does not change if COOP/COEP are
        // ever added for an unrelated reason.
        ort.env.wasm.numThreads = 1;
        // The load-bearing one. See the header: without the proxy worker, inference
        // runs on the main thread and the UI stops responding under sustained load.
        ort.env.wasm.proxy = true;
        ort.env.logLevel = 'error';
        return ort.InferenceSession.create(SILERO_MODEL_URL, {
            executionProviders: ['wasm'],
        });
    });
}
/**
 * Get the Silero runtime, creating it on first use.
 *
 * LAZY: nothing is fetched until a caller asks, so the 4.3 MB of runtime and
 * model never lands on the join or permissions screens.
 *
 * SINGLE-FLIGHT: concurrent callers share one in-progress initialisation, so two
 * simultaneous requests can never create two sessions — which would mean two
 * proxy workers and two copies of the model's WASM arena.
 *
 * Each successful call returns its own handle. The session is released when the
 * last handle is released, so one consumer releasing cannot pull the runtime out
 * from under another.
 */
export function getSileroRuntime() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (state.status === 'failed' && state.attempts >= SILERO_MAX_INIT_ATTEMPTS) {
            throw new Error((_a = state.error) !== null && _a !== void 0 ? _a : 'silero: runtime unavailable');
        }
        if (!state.pending) {
            state.attempts += 1;
            state.status = 'loading';
            state.error = null;
            state.pending = withTimeout(createSession(), SILERO_INIT_TIMEOUT_MS, 'initialisation');
        }
        let session;
        try {
            session = yield state.pending;
        }
        catch (error) {
            // Clear the cached attempt so a later caller MAY try again — a first failure
            // can be a cold cache or a transient fetch. `SILERO_MAX_INIT_ATTEMPTS` is
            // what stops that becoming unbounded.
            state.pending = null;
            state.status = 'failed';
            state.error = error instanceof Error ? error.message : String(error);
            throw error;
        }
        state.session = session;
        state.status = 'ready';
        state.refs += 1;
        return makeRuntime(session);
    });
}
/**
 * Wrap a session as the Phase 2 seam. Each handle releases at most once, and the
 * session itself is released only when the final handle lets go.
 */
function makeRuntime(session) {
    let released = false;
    return {
        tensor: (type, data, dims) => new state.ort.Tensor(type, data, dims),
        run: (feeds) => session.run(feeds),
        release: () => __awaiter(this, void 0, void 0, function* () {
            if (released)
                return;
            released = true;
            state.refs = Math.max(0, state.refs - 1);
            if (state.refs > 0)
                return;
            // Last handle out. Tensors are not disposed here: ORT reports
            // `location: 'cpu'` for everything this path creates, and their data are
            // plain JS TypedArrays owned by the caller, so there is no WASM allocation
            // to hand back. Only the session holds native resources.
            state.pending = null;
            state.session = null;
            state.status = 'idle';
            try {
                yield session.release();
            }
            catch (_a) {
                // A session already torn down by a page teardown is not worth surfacing.
            }
        }),
    };
}
